import { Box, MenuItem, Select } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { plural } from "@outschool/text";
import React, { useEffect, useState } from "react";

export const EnrollmentDurationSelect = ({
  enrollmentDuration,
  setEnrollmentDuration,
  disabled = false,
  isRecurringSchedulingAvailable = false,
  isPrepayEnrollment = false,
  isThreeStepBooking = false,
  durationsWithAvailability = [4, 8, 12]
}: {
  enrollmentDuration: number | null | undefined;
  setEnrollmentDuration: (val: number | null) => void;
  disabled?: boolean;
  isRecurringSchedulingAvailable?: boolean;
  isPrepayEnrollment?: boolean;
  isThreeStepBooking?: boolean;
  durationsWithAvailability?: number[];
}) => {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\EnrollmentDurationSelect"
  );
  const [durationOptions, setDurationOptions] = useState<
    {
      label: string;
      value: number | string;
    }[]
  >([]);

  const durationsWithAvailabilityDependency = JSON.stringify(
    durationsWithAvailability
  );

  useEffect(() => {
    if (isPrepayEnrollment) {
      const prePay = new Array(12).fill(0).map((_, i) => ({
        label: t("{{weekCount}} {{weeksLabel}}", {
          weekCount: i + 1,
          weeksLabel: plural("week", i + 1)
        }),
        value: i + 1
      }));
      setDurationOptions(prePay);
    } else {
      const getText = isThreeStepBooking
        ? (count: $TSFixMe) => {
            if (count === 1) {
              return t("Once");
            }
            return t("For {{weekCount}} sessions", {
              count,
              weekCount: count,
              defaultValue_plural: "For {{weekCount}} sessions"
            });
          }
        : (count: $TSFixMe) =>
            t("{{weekCount}} week", {
              count,
              weekCount: count,
              defaultValue_plural: "{{weekCount}} weeks"
            });

      const recurringOptions: {
        label: string;
        value: number | string;
      }[] = [
        ...durationsWithAvailability
          .sort((a, b) => b - a)
          .map(value => ({
            label: getText(value),
            value
          }))
      ];
      if (isRecurringSchedulingAvailable) {
        recurringOptions.unshift({
          label: isThreeStepBooking ? t`Until I cancel` : t`When I cancel`,
          value: "cancel"
        });
      }

      setDurationOptions(recurringOptions);
    }

    // `durationsWithAvailability` is an array, so we need to stringify to compare.
    // Otherwise it will infinitely loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    durationsWithAvailabilityDependency,
    isPrepayEnrollment,
    t,
    isRecurringSchedulingAvailable,
    isThreeStepBooking,
    setDurationOptions
  ]);

  return (
    <Box
      sx={(theme: $TSFixMe) => ({
        display: "flex",
        width: "100%",
        minWidth: "200px",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",

        [theme.breakpoints.up("md")]: {
          maxWidth: isThreeStepBooking ? "max-content" : "100%"
        }
      })}
    >
      <Select
        value={enrollmentDuration === null ? "cancel" : enrollmentDuration}
        sx={{ flex: 1 }}
        disabled={disabled}
        onChange={event => {
          const value = event.target.value;
          const enrollmentDuration =
            value === "cancel" || value === null
              ? null
              : parseInt(value as string, 10);
          setEnrollmentDuration(enrollmentDuration);
        }}
      >
        {durationOptions.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};
